import { ResourcesConfig } from 'aws-amplify';

const AppConfig = {
  backOfficeApiBaseURL: import.meta.env.VITE_BOA_API_BASE_URL,
  fflGetApiBaseURL: import.meta.env.VITE_FFLGET_API_BASE_URL,
  fflGetStoreApi: import.meta.env.VITE_STORE_API_BASE_URL,
  fflGetStoreApiKey: import.meta.env.VITE_STORE_API_KEY,
  fflGetMockAPI: import.meta.env.VITE_FFLGET_MOCK_API.toLowerCase() === 'true',
  backOfficeDefaultQuery: import.meta.env.VITE_BOA_EXPLORE_DEFAULT_QUERY,
  fflUILicenseApiBaseURL: import.meta.env.VITE_APP_GET_OCR_STEPFUNCTION_STATUS_BASE_URL,
  digitalAssetBucket: import.meta.env.VITE_DIGITAL_ASSET_BUCKET,
  preferencesBucket: import.meta.env.VITE_PREFERENCES_BUCKET,
  defaultStoreLogo: import.meta.env.VITE_DEFAULT_STORE_LOGO,
  licenseReaderBucket: import.meta.env.VITE_LICENSE_READER_BUCKET,
  masterFFLDigitalAssets: import.meta.env.VITE_MASTER_FFL_DIGITAL_ASSETS,
  SOTTaxStampsReaderBucket: import.meta.env.VITE_SOT_TAX_STAMPS_READER_BUCKET,
  SOTTaxStampsBaseURL: import.meta.env.VITE_SOT_TAX_STAMPS_BASE_URL,
  GMTId: import.meta.env.VITE_GTM_ID,
  intercomAppId: import.meta.env.VITE_INTERCOM_APP_ID,
  mapAPIKey: import.meta.env.VITE_GOOGLE_MAP_KEY,
  agGridLicenseKey: import.meta.env.VITE_AG_GRID_LICENSE_KEY,
  vite: {
    dev: import.meta.env.DEV,
    prod: import.meta.env.PROD,
    mode: import.meta.env.MODE
  },
  cookieStoreIdName: import.meta.env.VITE_COOKIE_STORE_ID_NAME,
  dsn: import.meta.env.VITE_SENTRY_DNS,
  environment: import.meta.env.VITE_ENV_MODE,
  org: import.meta.env.VITE_SENTRY_ORG,
  project: import.meta.env.VITE_SENTRY_PROJECT,
  fflExpressServiceApiURL: import.meta.env.VITE_FFL_EXPRESS_SERVICE_API_BASE_URL,
  transferProofLink: import.meta.env.VITE_TRANSFER_PROOF_LINK,
}

const getRedirectUrls = () => {
  const currentOrigin = window.location.origin;
  const devUrls = ['http://localhost:3000'];
  let prodUrls = import.meta.env.VITE_FFL360_DOMAIN ? [import.meta.env.VITE_FFL360_DOMAIN] : [];
  prodUrls.push(import.meta.env.VITE_MYFFL_DOMAIN)
  
  if (currentOrigin.includes('localhost')) {
    const currentUrl = currentOrigin;
    const currentUrlNoSlash = currentOrigin;
    
    if (!devUrls.includes(currentUrl)) {
      devUrls.push(currentUrl);
    }
    if (!devUrls.includes(currentUrlNoSlash)) {
      devUrls.push(currentUrlNoSlash);
    }
    return devUrls;
  }
  return [...devUrls, ...prodUrls];
};

const getRedirectSignInUrls = (isAutomation: boolean = false) => {
  const currentOrigin = isAutomation ? `${window.location.origin}/automationLogin` : window.location.origin;
  const baseUrls = [];
  if (currentOrigin.includes('localhost')) {
    baseUrls.push(currentOrigin);
    if (isAutomation) baseUrls.push(`${currentOrigin}/automationLogin`);
    if (!currentOrigin.includes(':3000')) {
      if (isAutomation) baseUrls.push('http://localhost:3000/automationLogin');
      else baseUrls.push('http://localhost:3000');
    }
  } else {
    const prodDomain = import.meta.env.VITE_FFL360_DOMAIN;
    if (prodDomain) {
      if (isAutomation) baseUrls.push(`${prodDomain}/automationLogin`);
      else baseUrls.push(prodDomain);
    }
    if (!baseUrls.includes(currentOrigin)) {
      if (isAutomation) baseUrls.push(`${currentOrigin}/automationLogin`);
      else baseUrls.push(currentOrigin);
    }
  }
  const uniqueUrls = [...new Set(baseUrls)];
  return uniqueUrls;
};

const getRedirectSignOutUrls = () => {
  const currentOrigin = window.location.origin;
  const baseUrls = [];
  if (currentOrigin.includes('localhost')) {
    baseUrls.push(currentOrigin);
    if (!currentOrigin.includes(':3000')) {
      baseUrls.push('http://localhost:3000');
    }
  } else {
    const prodDomain = import.meta.env.VITE_FFL360_DOMAIN;
    if (prodDomain) {
      baseUrls.push(prodDomain);
    }
    if (!baseUrls.includes(currentOrigin)) {
      baseUrls.push(currentOrigin);
    }
  }
  const uniqueUrls = [...new Set(baseUrls)];
  return uniqueUrls;
};

export const DynamicAmplifyConfiguration = (isAutomation: boolean = false) => {
  const redirectUrls = getRedirectSignInUrls(isAutomation);

  const AmplifyConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_AWS_USER_POOL_ID,
      userPoolClientId: isAutomation
        ? import.meta.env.VITE_AWS_USER_POOL_WEB_AUTOMATION_CLIENT_ID
        : import.meta.env.VITE_AWS_USER_POOL_WEB_CLIENT_ID,
      identityPoolId: import.meta.env.VITE_AWS_COGNITO_IDENTITY_POOL_ID,
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_COGNITO_DOMAIN,
          scopes: ['openid', 'aws.cognito.signin.user.admin'],
          redirectSignIn: redirectUrls,
          redirectSignOut: getRedirectSignOutUrls(),
        responseType: 'code'
        }
      },
      signUpVerificationMethod: 'code',
      userAttributes: {
        email: {
          required: true
        }
      },
      allowGuestAccess: false,
      passwordFormat: {
        minLength: 8,
        requireLowercase: false,
        requireUppercase: false,
        requireNumbers: false,
        requireSpecialCharacters: false,
      }
    }
  },
  Storage: {
    S3: {
      buckets: {
        boaBucket: {
          bucketName: import.meta.env.VITE_BOA_BUCKET,
          region: import.meta.env.VITE_AWS_COGNITO_REGION
        },
        digitalAssets: {
          bucketName: import.meta.env.VITE_DIGITAL_ASSET_BUCKET,
          region: import.meta.env.VITE_AWS_COGNITO_REGION
        },
        preferences: {
          bucketName: import.meta.env.VITE_PREFERENCES_BUCKET,
          region: import.meta.env.VITE_AWS_COGNITO_REGION
        },
        licenseReader: {
          bucketName: import.meta.env.VITE_LICENSE_READER_BUCKET,
          region: import.meta.env.VITE_AWS_COGNITO_REGION
        },
        masterFFLAssets: {
          bucketName: import.meta.env.VITE_MASTER_FFL_DIGITAL_ASSETS,
          region: import.meta.env.VITE_AWS_COGNITO_REGION
        },
        sotTaxStamps: {
          bucketName: import.meta.env.VITE_SOT_TAX_STAMPS_READER_BUCKET,
          region: import.meta.env.VITE_AWS_COGNITO_REGION
        },
        'miscellaneous': {
          bucketName: import.meta.env.VITE_MISCELLANEOUS_BUCKET,
          region: import.meta.env.VITE_AWS_COGNITO_REGION
        }
      }
    }
  }
};
return AmplifyConfig;
};

export default AppConfig;
